// Editors for the record-changing page-action steps — `txn_action`,
// `txn_update` and `refresh_source`. Object, action, form and column pickers
// are read from the project's real transaction model, so an author picks a
// button that exists instead of typing a name and hoping.

import { useEffect, useState } from 'react'
import { Plus, Trash2 } from 'lucide-react'
import { FIELD_LABEL, INPUT } from '../blockEditors'
import type { ActionStep, RefreshSourceStep, TxnActionStep, TxnUpdateStep } from '../types'
import { loadTransactionObject } from '@/api/designers'

export interface TxnStepCtx {
  project?: string
  transactions?: { obj_name: string; title: string }[]
  dataSources?: string[]
}

interface FormMeta { form_no: string; title: string; columns: string[]; actions: string[] }

const HINT: React.CSSProperties = { fontSize: '0.8125rem', color: 'var(--color-text-secondary)', marginTop: 2, lineHeight: 1.45 }
const TWO_COL: React.CSSProperties = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }

/** Forms (columns + action buttons) of one transaction object. The header
 *  form also lists the transaction-level actions, as the engine does. */
function useTxnMeta(project: string | undefined, obj: string | undefined): { forms: FormMeta[]; error: string } {
  const [forms, setForms] = useState<FormMeta[]>([])
  const [error, setError] = useState('')
  useEffect(() => {
    if (!project || !obj || obj.includes('{{')) { setForms([]); setError(''); return }
    let gone = false
    loadTransactionObject(project, obj)
      .then((res) => {
        if (gone) return
        const content = res?.content
        const t = content ? (content.transaction || content) : null
        const nameOf = (a: unknown) => {
          const x = a as { action?: { name?: string }; name?: string }
          return String(x?.action?.name ?? x?.name ?? '')
        }
        const txnActions = ((t?.actions as unknown[]) || []).map(nameOf).filter(Boolean)
        const out: FormMeta[] = []
        ;((t?.sql_models || []) as { sql_model?: Record<string, unknown> }[]).forEach((e, i) => {
          const sm = (e?.sql_model || e) as Record<string, unknown>
          const cols = ((sm.columns as unknown[]) || []).map((c) => {
            const col = (c as { column?: Record<string, unknown> }).column || (c as Record<string, unknown>)
            return String(col.name || col.db_name || '')
          }).filter(Boolean)
          const own = ((sm.actions as unknown[]) || []).map(nameOf).filter(Boolean)
          out.push({
            form_no: String(sm.form_no || i + 1),
            title: String(sm.form_title || sm.main_table || `Form ${i + 1}`),
            columns: cols,
            actions: i === 0 ? [...own, ...txnActions] : own,
          })
        })
        setForms(out)
        setError(out.length ? '' : 'This object has no forms.')
      })
      .catch(() => { if (!gone) { setForms([]); setError('Could not load this object.') } })
    return () => { gone = true }
  }, [project, obj])
  return { forms, error }
}

function ObjectPicker({ value, ctx, onChange }: { value: string; ctx: TxnStepCtx; onChange: (v: string) => void }) {
  const list = ctx.transactions || []
  if (!list.length) {
    return <input value={value} onChange={(e) => onChange(e.target.value)} placeholder="transaction object name" style={{ ...INPUT, fontFamily: 'monospace' }} />
  }
  const known = list.some((t) => t.obj_name === value)
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)} style={INPUT}>
      <option value="">— choose —</option>
      {value && !known && <option value={value}>{value} (not in this project)</option>}
      {list.map((t) => <option key={t.obj_name} value={t.obj_name}>{t.title || t.obj_name}</option>)}
    </select>
  )
}

function FormPicker({ value, forms, onChange }: { value?: string; forms: FormMeta[]; onChange: (v: string | undefined) => void }) {
  return (
    <select value={value || ''} onChange={(e) => onChange(e.target.value || undefined)} style={INPUT}>
      <option value="">Header form</option>
      {forms.slice(1).map((f) => <option key={f.form_no} value={f.form_no}>{f.title} (form {f.form_no})</option>)}
      {value && !forms.some((f) => f.form_no === value) && <option value={value}>form {value}</option>}
    </select>
  )
}

/** column → value rows (values support {{tokens}}). */
function ColumnValues({ label, value, columns, onChange, hint }: {
  label: string; value: Record<string, string> | undefined; columns: string[]
  onChange: (next: Record<string, string> | undefined) => void; hint?: string
}) {
  const entries = Object.entries(value || {})
  const listId = `cols-${label.replace(/\W/g, '')}`
  const setAt = (i: number, k: string, v: string) => {
    const next: Record<string, string> = {}
    entries.forEach(([kk, vv], j) => { if (j === i) next[k] = v; else next[kk] = vv })
    onChange(next)
  }
  return (
    <div>
      <label style={FIELD_LABEL}>{label}</label>
      <datalist id={listId}>{columns.map((c) => <option key={c} value={c} />)}</datalist>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {entries.map(([k, v], i) => (
          <div key={i} style={{ display: 'flex', gap: 6 }}>
            <input list={listId} value={k} onChange={(e) => setAt(i, e.target.value, v)} placeholder="COLUMN" style={{ ...INPUT, flex: 1, fontFamily: 'monospace' }} />
            <input value={v} onChange={(e) => setAt(i, k, e.target.value)} placeholder="value or {{token}}" style={{ ...INPUT, flex: 1.4 }} />
            <button
              onClick={(e) => {
                e.stopPropagation()
                const next = { ...(value || {}) }; delete next[k]
                onChange(Object.keys(next).length ? next : undefined)
              }}
              className="btn btn-ghost btn-sm" style={{ padding: 4, color: 'var(--color-error)' }} title="Remove"
            ><Trash2 size={13} /></button>
          </div>
        ))}
        <button
          onClick={(e) => { e.stopPropagation(); onChange({ ...(value || {}), '': '' }) }}
          className="btn btn-ghost btn-sm" style={{ alignSelf: 'flex-start' }}
        ><Plus size={13} /> Add column</button>
      </div>
      {hint && <div style={HINT}>{hint}</div>}
    </div>
  )
}

/** Which data sources to re-read afterwards: all (default) / none / chosen. */
function RefreshPicker({ value, ctx, onChange }: {
  value: string[] | undefined; ctx: TxnStepCtx; onChange: (v: string[] | undefined) => void
}) {
  const mode = value === undefined ? 'all' : value.length === 0 ? 'none' : 'some'
  const sources = ctx.dataSources || []
  return (
    <div>
      <label style={FIELD_LABEL}>Afterwards, re-read data</label>
      <select
        value={mode}
        onChange={(e) => {
          const m = e.target.value
          onChange(m === 'all' ? undefined : m === 'none' ? [] : (sources[0] ? [sources[0]] : []))
        }}
        style={INPUT}
      >
        <option value="all">Every runtime data source</option>
        <option value="some">Chosen data sources</option>
        <option value="none">Nothing</option>
      </select>
      {mode === 'some' && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 6 }}>
          {sources.map((id) => (
            <label key={id} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: '0.8625rem', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={(value || []).includes(id)}
                onChange={(e) => onChange(e.target.checked ? [...(value || []), id] : (value || []).filter((x) => x !== id))}
              />
              <code>{id}</code>
            </label>
          ))}
          {!sources.length && <span style={HINT}>This page has no data sources.</span>}
        </div>
      )}
    </div>
  )
}

export function TxnActionStepEditor({ step, ctx, onChange, renderBranches }: {
  step: TxnActionStep; ctx: TxnStepCtx; onChange: (s: TxnActionStep) => void
  renderBranches: (step: TxnActionStep, upd: (p: Partial<TxnActionStep>) => void) => React.ReactNode
}) {
  const upd = (p: Partial<TxnActionStep>) => onChange({ ...step, ...p })
  const { forms, error } = useTxnMeta(ctx.project, step.obj_name)
  const form = forms.find((f) => f.form_no === (step.form_no || forms[0]?.form_no))
  const actions = form?.actions || []
  return (
    <>
      <div style={TWO_COL}>
        <div>
          <label style={FIELD_LABEL}>Transaction</label>
          <ObjectPicker value={step.obj_name} ctx={ctx} onChange={(v) => upd({ obj_name: v, action: '' })} />
        </div>
        <div>
          <label style={FIELD_LABEL}>Form</label>
          <FormPicker value={step.form_no} forms={forms} onChange={(v) => upd({ form_no: v, action: '' })} />
        </div>
      </div>
      <div>
        <label style={FIELD_LABEL}>Action button</label>
        {actions.length ? (
          <select value={step.action} onChange={(e) => upd({ action: e.target.value })} style={INPUT}>
            <option value="">— choose —</option>
            {step.action && !actions.includes(step.action) && <option value={step.action}>{step.action} (not on this form)</option>}
            {actions.map((a) => <option key={a} value={a}>{a}</option>)}
          </select>
        ) : (
          <input value={step.action} onChange={(e) => upd({ action: e.target.value })} placeholder="action name" style={{ ...INPUT, fontFamily: 'monospace' }} />
        )}
        {error && step.obj_name && <div style={{ ...HINT, color: 'var(--color-error)' }}>{error}</div>}
      </div>
      <div>
        <label style={FIELD_LABEL}>Record id(s)</label>
        <input
          value={step.ids}
          onChange={(e) => upd({ ids: e.target.value })}
          placeholder="{{item._txn_id}}  or  {{selected_ids}}"
          style={{ ...INPUT, fontFamily: 'monospace' }}
        />
        <div style={HINT}>
          The clicked row (<code>{'{{alias._txn_id}}'}</code>) or a selection variable. More than one id runs the
          action as a bulk action; an empty selection does nothing.
        </div>
      </div>
      <RefreshPicker value={step.refresh} ctx={ctx} onChange={(refresh) => upd({ refresh })} />
      {renderBranches(step, upd)}
    </>
  )
}

export function TxnUpdateStepEditor({ step, ctx, onChange, renderBranches }: {
  step: TxnUpdateStep; ctx: TxnStepCtx; onChange: (s: TxnUpdateStep) => void
  renderBranches: (step: TxnUpdateStep, upd: (p: Partial<TxnUpdateStep>) => void) => React.ReactNode
}) {
  const upd = (p: Partial<TxnUpdateStep>) => onChange({ ...step, ...p })
  const { forms, error } = useTxnMeta(ctx.project, step.obj_name)
  const form = forms.find((f) => f.form_no === (step.form_no || forms[0]?.form_no))
  const columns = form?.columns || []
  return (
    <>
      <div style={TWO_COL}>
        <div>
          <label style={FIELD_LABEL}>Transaction</label>
          <ObjectPicker value={step.obj_name} ctx={ctx} onChange={(v) => upd({ obj_name: v })} />
        </div>
        <div>
          <label style={FIELD_LABEL}>Form</label>
          <FormPicker value={step.form_no} forms={forms} onChange={(v) => upd({ form_no: v })} />
        </div>
      </div>
      {error && step.obj_name && <div style={{ ...HINT, color: 'var(--color-error)' }}>{error}</div>}
      <div>
        <label style={FIELD_LABEL}>Record id</label>
        <input
          value={step.id}
          onChange={(e) => upd({ id: e.target.value })}
          placeholder="{{item._txn_id}}"
          style={{ ...INPUT, fontFamily: 'monospace' }}
        />
      </div>
      <ColumnValues
        label="Set columns"
        value={step.values}
        columns={columns}
        onChange={(values) => upd({ values: values || {} })}
        hint="Only these columns are written; validations, row security, approvals and the audit trail apply."
      />
      <ColumnValues
        label="Only if the stored record still has (optional)"
        value={step.expect}
        columns={columns}
        onChange={(expect) => upd({ expect })}
        hint="Compare-and-set: refused with “changed by someone else” when the stored value differs — two people tapping the same item cannot both win."
      />
      <RefreshPicker value={step.refresh} ctx={ctx} onChange={(refresh) => upd({ refresh })} />
      {renderBranches(step, upd)}
    </>
  )
}

export function RefreshSourceStepEditor({ step, ctx, onChange }: {
  step: RefreshSourceStep; ctx: TxnStepCtx; onChange: (s: RefreshSourceStep) => void
}) {
  const sources = ctx.dataSources || []
  const chosen = step.sources || []
  return (
    <div>
      <label style={FIELD_LABEL}>Data sources (none ticked = every runtime source)</label>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        {sources.map((id) => (
          <label key={id} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: '0.8625rem', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={chosen.includes(id)}
              onChange={(e) => {
                const next = e.target.checked ? [...chosen, id] : chosen.filter((x) => x !== id)
                onChange({ ...step, sources: next.length ? next : undefined })
              }}
            />
            <code>{id}</code>
          </label>
        ))}
        {!sources.length && <span style={HINT}>This page has no data sources.</span>}
      </div>
    </div>
  )
}

export type { ActionStep }
